import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { ArrowLeft, MapPinOff, AlertCircle, Plus, Minus, ShoppingBag, CalendarCheck } from 'lucide-react';
import { API_URL } from '../utils/api';
import { OrderTypeSheet } from '../components/OrderTypeSheet';
import { TableBookingSheet } from '../components/TableBookingSheet';
import FirebaseAuthModal from '../components/FirebaseAuthModal';
import { EmptyState } from '../components/ui/EmptyState';
import { Button } from '../components/ui/Button';
import { Pill } from '../components/ui/Pill';
import { useCustomerAuth } from '../contexts/CustomerAuthContext';
import { usePwaNav } from '../contexts/PwaNavContext';
import './OrderStatus.css';

const tintFor = (cat?: string): string => {
  const c = (cat || '').toLowerCase().trim();
  if (c.includes('drink') || c.includes('напит') || c.includes('сусын') || c.includes('beverage')) return '#E98A6A';
  if (c.includes('dessert') || c.includes('десерт')) return '#EBB86A';
  if (c.includes('salad') || c.includes('салат')) return '#C8986A';
  if (c.includes('soup') || c.includes('суп') || c.includes('сорпа')) return '#F0D89A';
  if (c.includes('pizza') || c.includes('пицца')) return '#E8A87C';
  if (c.includes('burger') || c.includes('бургер')) return '#D4A056';
  return '#F4C77A';
};
import './RestaurantMenuPage.css';

interface MenuProduct {
  id: string;
  name: string;
  description: string | null;
  price: number;
  imageUrl: string | null;
  category: string | null;
  categorySort?: number | null;
}

interface RestaurantPublicResponse {
  id: string;
  name: string;
  slug: string;
  logoUrl: string | null;
  coverImageUrl: string | null;
  cuisine: string | null;
  openNow: boolean;
  opensAt: string | null;
  todayHours: { open: string; close: string } | null;
  dineInSupported: boolean;
  acceptsPreorders: boolean;
  tableCount: number;
  products: MenuProduct[];
}

type LoadState =
  | { status: 'loading' }
  | { status: 'ok'; data: RestaurantPublicResponse }
  | { status: '404' }
  | { status: 'error' };

const CATEGORY_LABEL_RU: Record<string, string> = {
  main: 'Основные блюда',
  side: 'Гарниры и закуски',
  drink: 'Напитки',
  dessert: 'Десерты',
};
const CATEGORY_ORDER = ['main', 'side', 'drink', 'dessert'];

const cartKey = (restaurantId: string) => `olso_cart_${restaurantId}`;

export default function RestaurantMenuPage() {
  const { t } = useTranslation();
  const params = useParams<{ restaurantSlug: string }>();
  const { navData, navigate: pwaNavigate } = usePwaNav();
  // In PWA standalone mode the URL doesn't change, so useParams is empty;
  // fall back to navData.restaurantSlug set by navigate() in PwaNavContext.
  const restaurantSlug = params.restaurantSlug ?? navData?.restaurantSlug;
  const goHome = () => pwaNavigate('/');
  const { customer } = useCustomerAuth();
  const [state, setState] = useState<LoadState>({ status: 'loading' });
  const [reloadKey, setReloadKey] = useState(0);
  const [showOrderTypeSheet, setShowOrderTypeSheet] = useState(false);
  const [showBookingSheet, setShowBookingSheet] = useState(false);
  const [showAuth, setShowAuth] = useState(false);
  // cart: productId → quantity
  const [cart, setCart] = useState<Record<string, number>>({});

  // Fetch restaurant + products
  useEffect(() => {
    if (!restaurantSlug) {
      setState({ status: '404' });
      return;
    }
    let cancelled = false;
    setState({ status: 'loading' });
    axios
      .get<RestaurantPublicResponse>(
        `${API_URL}/api/restaurants/by-slug/${restaurantSlug}/public`
      )
      .then((res) => {
        if (!cancelled) setState({ status: 'ok', data: res.data });
      })
      .catch((err) => {
        if (cancelled) return;
        if (err?.response?.status === 404) setState({ status: '404' });
        else setState({ status: 'error' });
      });
    return () => {
      cancelled = true;
    };
  }, [restaurantSlug, reloadKey]);

  // Restore cart from sessionStorage when restaurant loads
  useEffect(() => {
    if (state.status !== 'ok') return;
    try {
      const raw = sessionStorage.getItem(cartKey(state.data.id));
      if (raw) setCart(JSON.parse(raw));
    } catch { /* ignore */ }
  }, [state.status, state.status === 'ok' ? state.data.id : '']);

  // Persist cart to sessionStorage
  useEffect(() => {
    if (state.status !== 'ok') return;
    try {
      sessionStorage.setItem(cartKey(state.data.id), JSON.stringify(cart));
    } catch { /* ignore */ }
  }, [cart, state.status, state.status === 'ok' ? state.data.id : '']);

  const productsByCategory = useMemo(() => {
    if (state.status !== 'ok' || !Array.isArray(state.data.products)) return {} as Record<string, MenuProduct[]>;
    const grouped: Record<string, MenuProduct[]> = {};
    state.data.products.forEach((p) => {
      const cat = p.category || 'main';
      if (!grouped[cat]) grouped[cat] = [];
      grouped[cat].push(p);
    });
    return grouped;
  }, [state]);

  const orderedCategories = useMemo(() => {
    const cats = Object.keys(productsByCategory);
    const sortOf = (cat: string) => {
      const sorts = productsByCategory[cat].map((p) => p.categorySort).filter((s): s is number => s != null);
      return sorts.length ? Math.min(...sorts) : null;
    };
    return cats.sort((a, b) => {
      const sa = sortOf(a), sb = sortOf(b);
      if (sa != null && sb != null) return sa - sb;
      if (sa != null) return -1;
      if (sb != null) return 1;
      const ia = CATEGORY_ORDER.indexOf(a), ib = CATEGORY_ORDER.indexOf(b);
      if (ia !== -1 || ib !== -1) return (ia === -1 ? 99 : ia) - (ib === -1 ? 99 : ib);
      return a.localeCompare(b);
    });
  }, [productsByCategory]);

  const cartCount = useMemo(
    () => Object.values(cart).reduce((sum, q) => sum + q, 0),
    [cart]
  );

  const cartTotal = useMemo(() => {
    if (state.status !== 'ok' || !Array.isArray(state.data.products)) return 0;
    return state.data.products.reduce((sum, p) => sum + (cart[p.id] || 0) * p.price, 0);
  }, [cart, state]);

  const cartItems = useMemo(() => {
    if (state.status !== 'ok' || !Array.isArray(state.data.products)) return [];
    return state.data.products
      .filter((p) => (cart[p.id] || 0) > 0)
      .map((p) => ({ productId: p.id, quantity: cart[p.id], price: p.price, name: p.name }));
  }, [cart, state]);

  const inc = (productId: string) => setCart((c) => ({ ...c, [productId]: (c[productId] || 0) + 1 }));
  const dec = (productId: string) =>
    setCart((c) => {
      const next = { ...c };
      const q = (next[productId] || 0) - 1;
      if (q <= 0) delete next[productId];
      else next[productId] = q;
      return next;
    });

  if (state.status === 'loading') {
    return (
      <main className="rm-page" aria-busy="true">
        <div className="rm-skeleton rm-skeleton--cover" />
        <div className="rm-content">
          {[0, 1, 2, 3].map((i) => (
            <div key={i} className="rm-skeleton rm-skeleton--row" />
          ))}
        </div>
      </main>
    );
  }

  if (state.status === '404') {
    return (
      <main className="rm-page rm-page--centered">
        <EmptyState
          icon={<MapPinOff aria-hidden="true" />}
          title={t('restaurantMenu.notFoundTitle')}
          action={
            <Button variant="secondary" onClick={goHome}>
              {t('restaurantMenu.notFoundCta')}
            </Button>
          }
        />
      </main>
    );
  }

  if (state.status === 'error') {
    return (
      <main className="rm-page rm-page--centered" role="alert">
        <EmptyState
          icon={<AlertCircle aria-hidden="true" />}
          title={t('restaurantMenu.errorTitle')}
          action={
            <Button variant="secondary" onClick={() => setReloadKey((k) => k + 1)}>
              {t('restaurantMenu.errorCta')}
            </Button>
          }
        />
      </main>
    );
  }

  const restaurant = state.data;
  const ctaLabel = restaurant.openNow
    ? cartCount > 0
      ? `${t('restaurantMenu.orderCta')} · ${cartTotal.toLocaleString('ru')} ₸`
      : t('restaurantMenu.orderCta')
    : restaurant.opensAt
      ? t('restaurantMenu.orderDisabledCta', { time: restaurant.opensAt })
      : t('restaurantMenu.orderDisabledCta', { time: '' });

  return (
    <main className="rm-page">
      {/* Cover */}
      <div className="rm-cover">
        {restaurant.coverImageUrl ? (
          <img src={restaurant.coverImageUrl} alt="" className="rm-cover-img" />
        ) : (
          <div className="rm-cover-fallback" aria-hidden="true" />
        )}
        <div className="rm-cover-grad" aria-hidden="true" />
        <button
          className="rm-cover-back"
          type="button"
          onClick={goHome}
          aria-label={t('venueLanding.back', { defaultValue: 'Назад' })}
        >
          <ArrowLeft size={18} aria-hidden="true" />
        </button>
      </div>

      {/* Header card overlapping cover */}
      <header className="rm-header">
        <div className="rm-header-logo">
          {restaurant.logoUrl ? (
            <img src={restaurant.logoUrl} alt="" className="rm-logo-img" />
          ) : (
            <div className="rm-logo-fallback" aria-hidden="true">
              {restaurant.name.charAt(0).toUpperCase()}
            </div>
          )}
        </div>
        <div className="rm-header-text">
          <div className="rm-name">{restaurant.name}</div>
          {restaurant.cuisine && (
            <div className="rm-cuisine">
              {t(`cuisine.${restaurant.cuisine}`, { defaultValue: restaurant.cuisine })}
              {restaurant.dineInSupported && restaurant.tableCount > 0 && (
                <> · {t('venueLanding.tablesShort', { defaultValue: 'столов' })} {restaurant.tableCount}</>
              )}
            </div>
          )}
        </div>
        <div className="rm-header-pill">
          <Pill tone={restaurant.openNow ? 'green' : 'neutral'}>
            {restaurant.openNow
              ? t('venueLanding.open')
              : t('venueLanding.closed')}
          </Pill>
        </div>
      </header>

      {/* Category tabs (sticky) */}
      {orderedCategories.length > 1 && (
        <nav className="rm-cat-tabs" aria-label="Категории">
          {orderedCategories.map((cat) => (
            <a
              key={cat}
              href={`#cat-${cat}`}
              className="rm-cat-tab"
              onClick={(e) => {
                e.preventDefault();
                document.getElementById(`cat-${cat}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
              }}
            >
              {CATEGORY_LABEL_RU[cat] || cat}
            </a>
          ))}
        </nav>
      )}

      {/* Menu */}
      <div className="rm-content">
        {orderedCategories.length === 0 ? (
          <EmptyState
            icon={<ShoppingBag aria-hidden="true" />}
            title={t('restaurantMenu.emptyTitle')}
            sub={t('restaurantMenu.emptySub')}
          />
        ) : (
          orderedCategories.map((cat) => (
            <section key={cat} id={`cat-${cat}`} className="rm-cat">
              <h2 className="rm-cat-title">{CATEGORY_LABEL_RU[cat] || cat}</h2>
              <div className="os-upsell-grid rm-cat-grid">
                {productsByCategory[cat].map((p) => {
                  const qty = cart[p.id] || 0;
                  const tint = tintFor(p.category ?? undefined);
                  const tintAlt = tint + 'CC';
                  const mediaStyle: React.CSSProperties = p.imageUrl
                    ? { backgroundImage: `url(${p.imageUrl})` }
                    : ({ ['--os-card-tint' as string]: tint, ['--os-card-tint-2' as string]: tintAlt } as React.CSSProperties);
                  const categoryLabel = CATEGORY_LABEL_RU[p.category || ''] || p.category || '';
                  return (
                    <div key={p.id} className="os-upsell-card">
                      <div
                        className={`os-upsell-media${p.imageUrl ? ' os-upsell-media--img' : ''}`}
                        style={mediaStyle}
                      >
                        {categoryLabel && <span className="os-upsell-cat-pill">{categoryLabel}</span>}
                      </div>
                      <div className="os-upsell-body">
                        <div className="os-upsell-name">{p.name}</div>
                        {p.description && (
                          <div className="os-upsell-desc">{p.description}</div>
                        )}
                        <div className="os-upsell-footer">
                          <span className="os-upsell-price">₸{p.price.toLocaleString('ru')}</span>
                          {qty === 0 ? (
                            <button
                              type="button"
                              className="os-upsell-add"
                              onClick={() => inc(p.id)}
                              disabled={!restaurant.openNow}
                              aria-label={`Добавить ${p.name}`}
                            >
                              <Plus size={14} strokeWidth={2.5} />
                            </button>
                          ) : (
                            <div className="os-upsell-qty">
                              <button
                                type="button"
                                className="os-upsell-qty-btn"
                                onClick={() => dec(p.id)}
                                aria-label="Убрать"
                              >
                                <Minus size={12} strokeWidth={2.5} />
                              </button>
                              <span className="os-upsell-count">{qty}</span>
                              <button
                                type="button"
                                className="os-upsell-qty-btn is-add"
                                onClick={() => inc(p.id)}
                                aria-label="Добавить"
                              >
                                <Plus size={12} strokeWidth={2.5} />
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </section>
          ))
        )}
      </div>

      {/* Sticky cart bar */}
      {(cartCount > 0 || restaurant.openNow) && (
        <div className={`rm-cart-bar ${cartCount > 0 ? 'rm-cart-bar--active' : ''}`}>
          {cartCount > 0 && (
            <div className="rm-cart-bar-badge">
              <ShoppingBag size={16} aria-hidden="true" />
              <span>{cartCount}</span>
            </div>
          )}
          <Button
            variant="primary"
            size="lg"
            className="rm-cart-bar-btn"
            disabled={!restaurant.openNow || cartCount === 0}
            aria-disabled={!restaurant.openNow || cartCount === 0}
            onClick={() => setShowOrderTypeSheet(true)}
          >
            {ctaLabel}
          </Button>
        </div>
      )}

      {/* Floating "Book table" button — opens booking flow (not order checkout) */}
      {restaurant.dineInSupported && restaurant.tableCount > 0 && restaurant.openNow && (
        <button
          type="button"
          className={`rm-book-fab ${cartCount > 0 ? 'rm-book-fab--lifted' : ''}`}
          onClick={() => setShowBookingSheet(true)}
          aria-label={t('restaurantMenu.bookTable', { defaultValue: 'Забронировать столик' })}
        >
          <CalendarCheck size={20} strokeWidth={2.2} aria-hidden="true" />
          <span className="rm-book-fab-label">
            {t('restaurantMenu.bookTable', { defaultValue: 'Забронировать столик' })}
          </span>
        </button>
      )}

      <OrderTypeSheet
        open={showOrderTypeSheet}
        onClose={() => setShowOrderTypeSheet(false)}
        restaurant={{
          id: restaurant.id,
          dineInSupported: restaurant.dineInSupported,
          acceptsPreorders: restaurant.acceptsPreorders,
          tableCount: restaurant.tableCount,
          todayHours: restaurant.todayHours,
        }}
        items={cartItems}
        customerId={customer?.id ?? null}
        fcmToken={null}
        onSignInRequired={() => setShowAuth(true)}
      />

      <TableBookingSheet
        open={showBookingSheet}
        onClose={() => setShowBookingSheet(false)}
        restaurant={{
          id: restaurant.id,
          tableCount: restaurant.tableCount,
          todayHours: restaurant.todayHours,
        }}
        customerName={customer?.name ?? null}
        customerPhone={customer?.phone ?? null}
        onSignInRequired={() => setShowAuth(true)}
      />

      {showAuth && (
        <FirebaseAuthModal
          onClose={() => setShowAuth(false)}
          onSuccess={() => setShowAuth(false)}
        />
      )}
    </main>
  );
}
